import { useState } from "react";
import { playNow, kickAudio } from "@/audio/piano";

/**
 * IntervalExplorer — every interval from unison to octave, click to hear.
 *
 * Used in lessons 7, 8, 13 (Remembering Intervals · Theory of Intervals ·
 * Circle of Fifths for Intervals). The colour-coding follows the lesson
 * vocabulary: resolutions = green, anticipations = amber, tensions = rose,
 * mysteries = purple.
 */

interface IntervalSpec {
  semitones: number;
  short: string;
  name: string;
  /** "Mood" category as taught in the course. */
  mood: "resolution" | "anticipation" | "tension" | "mystery";
  description: string;
  songCue?: string;
}

const INTERVALS: IntervalSpec[] = [
  { semitones: 0, short: "P1", name: "Unison",            mood: "resolution",   description: "Same note. The most stable interval." },
  { semitones: 1, short: "m2", name: "Minor 2nd",         mood: "tension",      description: "Half-step apart. The sharpest clash in music — and the whole horror genre.",     songCue: "Jaws theme" },
  { semitones: 2, short: "M2", name: "Major 2nd",         mood: "anticipation", description: "Whole-step. Used for stepwise motion.",   songCue: "Happy Birthday (1→2)" },
  { semitones: 3, short: "m3", name: "Minor 3rd",         mood: "resolution",   description: "The 'sad' third. Sits inside every minor chord.", songCue: "Greensleeves" },
  { semitones: 4, short: "M3", name: "Major 3rd",         mood: "resolution",   description: "The 'happy' third. Sits inside every major chord.", songCue: "When the Saints" },
  { semitones: 5, short: "P4", name: "Perfect 4th",       mood: "anticipation", description: "Open and firm on its own, but leans toward the 3rd when a chord sits under it.",   songCue: "Here Comes the Bride" },
  { semitones: 6, short: "TT", name: "Tritone (♯4 / ♭5)", mood: "tension",      description: "Six semitones — exactly half an octave. Maximum dissonance. The 'devil's interval' — also Lydian's dreaminess, also heavy-metal's bite.", songCue: "Maria from West Side Story" },
  { semitones: 7, short: "P5", name: "Perfect 5th",       mood: "resolution",   description: "Most stable beyond unison/octave. Open, bold — the spine of every chord.", songCue: "Twinkle Twinkle (1→5)" },
  { semitones: 8, short: "m6", name: "Minor 6th",         mood: "mystery",      description: "Wistful, cinematic, ambiguous. Sometimes a destination, sometimes a stepping-stone — depends entirely on the chord beneath it.", songCue: "The Entertainer" },
  { semitones: 9, short: "M6", name: "Major 6th",         mood: "mystery",      description: "Warm, pastoral, dual-function. Can feel resolved like a 3rd or restless like a 7th — the harmony beneath decides.", songCue: "My Bonnie" },
  { semitones: 10, short: "m7", name: "Minor 7th",        mood: "anticipation", description: "The great bridge note. Glides back to the 6th or up to the octave. The colour note of blues, funk, and every dominant chord.", songCue: "Somewhere (West Side Story)" },
  { semitones: 11, short: "M7", name: "Major 7th",        mood: "tension",      description: "Just shy of the octave. Cinematic, lush.",   songCue: "Take On Me hook" },
  { semitones: 12, short: "P8", name: "Perfect Octave",   mood: "resolution",   description: "Same letter, double the frequency. Maximum consonance after unison.", songCue: "Somewhere Over the Rainbow (1→8)" },
];

const MOOD_COLOURS: Record<IntervalSpec["mood"], { bg: string; text: string; border: string }> = {
  resolution:   { bg: "bg-emerald-500/10", text: "text-emerald-300", border: "border-emerald-500/40" },
  anticipation: { bg: "bg-amber-500/10",   text: "text-amber-300",   border: "border-amber-500/40"   },
  tension:      { bg: "bg-rose-500/10",    text: "text-rose-300",    border: "border-rose-500/40"    },
  mystery:      { bg: "bg-purple-500/10",  text: "text-purple-300",  border: "border-purple-500/40"  },
};

const NOTE_NAMES_SHARP = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

interface IntervalExplorerProps {
  /** Root note for all intervals (default C). */
  root?: string;
  /** Octave of the root (default 4). */
  octave?: number;
  /** Show the upper note + interval together as a chord, or sequentially. */
  mode?: "harmonic" | "melodic";
}

export function IntervalExplorer({
  root: initialRoot = "C",
  octave = 4,
  mode: initialMode = "harmonic",
}: IntervalExplorerProps) {
  const [root, setRoot] = useState(initialRoot);
  const [mode, setMode] = useState(initialMode);
  const rootSemi = NOTE_NAMES_SHARP.indexOf(root) >= 0 ? NOTE_NAMES_SHARP.indexOf(root) : 0;

  const playInterval = (semitones: number) => {
    kickAudio();
    const lower = `${NOTE_NAMES_SHARP[rootSemi]}${octave}`;
    const upperSemi = rootSemi + semitones;
    const upper = `${NOTE_NAMES_SHARP[upperSemi % 12]}${octave + Math.floor(upperSemi / 12)}`;
    if (mode === "harmonic") {
      void playNow([lower, upper], "2n", 0.7);
    } else {
      void playNow(lower, "8n", 0.7);
      setTimeout(() => void playNow(upper, "8n", 0.7), 350);
    }
  };

  return (
    <div className="my-5 rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-500/[0.04] to-emerald-500/[0.03] p-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-rose-300">
            Interval explorer
          </div>
          <div className="mt-1 font-mt text-xl text-nsm-ink">
            From <span className="font-semibold text-rose-300">{root}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-full border border-nsm-border bg-zinc-900/40 p-1 text-[11px]">
            <button
              onClick={() => setMode("harmonic")}
              className={`rounded-full px-3 py-1.5 transition ${
                mode === "harmonic" ? "bg-rose-500/30 text-rose-100" : "text-nsm-dim hover:text-nsm-ink"
              }`}
            >
              ⏛ Harmonic
            </button>
            <button
              onClick={() => setMode("melodic")}
              className={`rounded-full px-3 py-1.5 transition ${
                mode === "melodic" ? "bg-rose-500/30 text-rose-100" : "text-nsm-dim hover:text-nsm-ink"
              }`}
            >
              → Melodic
            </button>
          </div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-1">
        <span className="mr-1 text-[10px] uppercase tracking-wider text-nsm-dim">Root:</span>
        {["C", "D", "E", "F", "G", "A", "B"].map((r) => (
          <button
            key={r}
            onClick={() => setRoot(r)}
            className={`min-w-[32px] rounded px-2 py-1.5 text-[12px] font-semibold transition ${
              r === root ? "bg-rose-500/30 text-rose-100" : "bg-zinc-800/50 text-nsm-dim hover:text-nsm-ink"
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      <div className="grid gap-2 md:grid-cols-2">
        {INTERVALS.map((iv) => {
          const c = MOOD_COLOURS[iv.mood];
          return (
            <button
              key={iv.semitones}
              onClick={() => playInterval(iv.semitones)}
              className={`group rounded-xl border ${c.border} ${c.bg} p-3 text-left transition hover:scale-[1.01]`}
            >
              <div className="flex items-baseline justify-between">
                <div className="font-mt text-base font-semibold text-nsm-ink">
                  {iv.name}
                </div>
                <span className={`text-[10px] uppercase tracking-wider ${c.text}`}>
                  {iv.short} · {iv.semitones} st
                </span>
              </div>
              <div className="mt-1 text-[12px] leading-snug text-nsm-dim">
                {iv.description}
              </div>
              {iv.songCue && (
                <div className="mt-1 text-[11px] italic text-amber-300/80">
                  ♪ {iv.songCue}
                </div>
              )}
              <div className={`mt-1 text-[10px] uppercase tracking-wider ${c.text}`}>
                {iv.mood}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-wider text-nsm-dim">
        <span><span className="text-emerald-300">●</span> resolution</span>
        <span><span className="text-amber-300">●</span> anticipation</span>
        <span><span className="text-rose-300">●</span> tension</span>
        <span><span className="text-purple-300">●</span> mystery</span>
      </div>
    </div>
  );
}
